import {
    ATTRIBUTE_VIEW_TABS,
    LOCATION_TABS,
    type AttributeView,
    type LocationTab,
    type Tab,
    TABS,
} from "@/constants";
import { getIndex } from "@/repo";
import { useQuery } from "@tanstack/react-query";
import {
    type RefObject,
    type MutableRefObject,
    useState,
    useRef,
    useEffect,
} from "react";
import TabList from "./TabList";
import {
    useLocation,
    useNavigate,
    Routes,
    Route,
    Navigate,
} from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { HelpCircle } from "lucide-react";
import { setEnv, type TileEnv } from "@/env";

import "./ControlPanel.css";
import type { Map as MapboxMap } from "mapbox-gl";
import PathImg from "../assets/tilde.png";
import LocationCollectionList from "./ControlPanelLocation/LocationCollectionList";
import LocationAttributeList from "./ControlPanelLocation/LocationAttributeList";
import LocationIndicatorList from "./ControlPanelLocation/LocationIndicatorList";
import LocationPlaceAttributeList from "./ControlPanelLocation/LocationPlaceAttributeList";
import Finance from "./ControlPanelFinance/Finance";
import Rank from "./ControlPanelRank/Rank";
import type { RankingContext, ScoreBreakdown } from "@/repo/rank";
import Property from "./ControlPanelProperty/Property";

interface ControlPanelProps {
    map: RefObject<MapboxMap | undefined>;
    rankingContextRef: MutableRefObject<RankingContext>;
    onSelectScore: (score: ScoreBreakdown | null) => void;
}

const TAB_DEFAULTS: Record<Tab, string> = {
    location: "/location/attributes/token",
    finance: "/finance/tokens",
    property: "/property",
    rank: "/rank",
};

const LOCATION_TAB_DEFAULTS: Record<LocationTab, string> = {
    indicators: "/location/indicators",
    collections: "/location/collections",
    attributes: "/location/attributes/token",
};

function ControlPanel(props: ControlPanelProps) {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [env, setEnvState] = useState<TileEnv>("dev");
    const [helpOpen, setHelpOpen] = useState(false);
    const helpRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!helpOpen) return;
        function handleClick(e: MouseEvent) {
            if (
                helpRef.current &&
                !helpRef.current.contains(e.target as Node)
            ) {
                setHelpOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [helpOpen]);

    function switchEnv(next: TileEnv) {
        setEnv(next);
        setEnvState(next);
        queryClient.invalidateQueries();
    }

    const parts = pathname.split("/").filter(Boolean);
    const tab = (parts[0] as Tab) || "rank";
    const locationTab =
        tab === "location"
            ? (parts[1] as LocationTab) || "attributes"
            : "attributes";
    const attributeView =
        tab === "location" && locationTab === "attributes"
            ? (parts[2] as AttributeView) || "token"
            : "token";

    const query = useQuery({
        queryKey: ["index"],
        queryFn: getIndex,
    });

    if (query.isPending) return null;
    if (query.error) return <div>Error: {query.error.message}</div>;

    return (
        <div className="control-panel">
            <div className="control-panel__top">
                <div className="control-panel__section control-panel-header">
                    <img src={PathImg} alt="Path logo" />
                    <h3>Explorer</h3>
                    <div className="env-switch">
                        <button
                            className={`env-switch__btn${env === "dev" ? " env-switch__btn--active" : ""}`}
                            onClick={() => switchEnv("dev")}
                        >
                            DEV
                        </button>
                        <button
                            className={`env-switch__btn${env === "prod" ? " env-switch__btn--active" : ""}`}
                            onClick={() => switchEnv("prod")}
                        >
                            PROD
                        </button>
                    </div>
                    <div className="env-help" ref={helpRef}>
                        <button
                            className="env-help__btn"
                            onClick={() => setHelpOpen((v) => !v)}
                            aria-label="Environment help"
                        >
                            <HelpCircle size={14} />
                        </button>
                        {helpOpen && (
                            <div className="env-help__popover">
                                <p>
                                    Switching environments changes the data
                                    source for:
                                </p>
                                <ul>
                                    <li>Attributes</li>
                                    <li>Ranking</li>
                                    <li>Finance information</li>
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
                <hr />
                <TabList
                    key="main"
                    tabs={TABS}
                    current={tab}
                    setCurrent={(t) => navigate(TAB_DEFAULTS[t])}
                />

                {tab === "location" && (
                    <TabList
                        key="location-sub"
                        tabs={LOCATION_TABS}
                        current={locationTab}
                        setCurrent={(lt) => navigate(LOCATION_TAB_DEFAULTS[lt])}
                    />
                )}

                {tab === "location" && locationTab === "attributes" && (
                    <TabList
                        key="attribute-view"
                        tabs={ATTRIBUTE_VIEW_TABS}
                        current={attributeView}
                        setCurrent={(av) => {
                            const selectedAttrId = parts[3];
                            navigate(
                                selectedAttrId
                                    ? `/location/attributes/${av}/${selectedAttrId}`
                                    : `/location/attributes/${av}`,
                            );
                        }}
                    />
                )}
            </div>

            {query.data && (
                <Routes>
                    <Route path="/" element={<Navigate to="/rank" replace />} />
                    <Route
                        path="/location/attributes/token"
                        element={
                            <LocationAttributeList
                                attributes={query.data.attributes}
                                map={props.map}
                                env={env}
                            />
                        }
                    />
                    <Route
                        path="/location/attributes/token/:id"
                        element={
                            <LocationAttributeList
                                attributes={query.data.attributes}
                                map={props.map}
                                env={env}
                            />
                        }
                    />
                    <Route
                        path="/location/attributes/place"
                        element={
                            <LocationPlaceAttributeList
                                attributes={query.data.attributes}
                                map={props.map}
                                env={env}
                            />
                        }
                    />
                    <Route
                        path="/location/attributes/place/:id"
                        element={
                            <LocationPlaceAttributeList
                                attributes={query.data.attributes}
                                map={props.map}
                                env={env}
                            />
                        }
                    />
                    <Route
                        path="/location/indicators"
                        element={
                            <LocationIndicatorList
                                indicators={query.data.indicators}
                                map={props.map}
                                env={env}
                            />
                        }
                    />
                    <Route
                        path="/location/indicators/:id"
                        element={
                            <LocationIndicatorList
                                indicators={query.data.indicators}
                                map={props.map}
                                env={env}
                            />
                        }
                    />
                    <Route
                        path="/location/collections"
                        element={
                            <LocationCollectionList
                                collections={query.data.collections}
                                map={props.map}
                                env={env}
                            />
                        }
                    />
                    <Route
                        path="/location/collections/:id"
                        element={
                            <LocationCollectionList
                                collections={query.data.collections}
                                map={props.map}
                                env={env}
                            />
                        }
                    />
                    <Route
                        path="/finance/:view"
                        element={<Finance map={props.map} env={env} />}
                    />
                    <Route
                        path="/finance"
                        element={<Navigate to="/finance/tokens" replace />}
                    />
                    <Route
                        path="/rank"
                        element={
                            <Rank
                                attributes={query.data.attributes}
                                map={props.map}
                                rankingContextRef={props.rankingContextRef}
                                onSelectScore={props.onSelectScore}
                                env={env}
                            />
                        }
                    />
                    <Route
                        path="/property"
                        element={<Property map={props.map} />}
                    />
                    <Route
                        path="*"
                        element={<Navigate to="/location/indicators" replace />}
                    />
                </Routes>
            )}
        </div>
    );
}

export default ControlPanel;
